// src/pages/printPOSupplierPage.js
import React, { useEffect, useState } from 'react';
import printJS from 'print-js';
import './printPOSupplierPage.css';
import { useAuth } from '../context/AuthContext';
import { getPedidosBySupplier } from '../services/pedidosService';

function PrintPOSupplierPage() {
    const { isLoadingAuth, interceptorsReady } = useAuth();
    
    const [supplierData, setSupplierData] = useState(null);
    const [summary, setSummary] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentDate, setCurrentDate] = useState('');

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit' 
        });
    };

    // Format price
    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) return '$ 0,00';
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Get status label
    const getStatusLabel = (status) => {
        const labels = {
            'PENDIENTE': 'Pendiente',
            'APROBADO': 'Aprobado',
            'EN_TRANSITO': 'En Tránsito',
            'RECIBIDO': 'Recibido',
            'PARCIAL': 'Parcial',
            'CANCELADO': 'Cancelado'
        };
        return labels[status] || status;
    };

    // Print function
    const handlePrint = () => {
        printJS({
            printable: 'print-po-supplier-content',
            type: 'html',
            scanStyles: false,
            style: `
                @page { 
                    margin: 15mm; 
                    size: A4;
                }
                
                * { 
                    box-sizing: border-box; 
                    margin: 0; 
                    padding: 0; 
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                    color-adjust: exact;
                }
                
                @media print {
                    * {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        color-adjust: exact !important;
                    }
                }
                
                body { 
                    margin: 0 !important; 
                    padding: 0 !important; 
                    font-family: 'Arial', sans-serif;
                    font-size: 10pt;
                    color: #000;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                
                .print-po-supplier-container {
                    width: 100%;
                    padding: 10mm;
                    background: #fff;
                    color: #000;
                }
                
                .po-header {
                    text-align: center;
                    margin-bottom: 20px;
                    border-bottom: 2px solid #000;
                    padding-bottom: 10px;
                }
                
                .po-header h1 {
                    font-size: 18pt;
                    margin-bottom: 5px;
                    color: #000;
                }
                
                .po-header .subtitle {
                    font-size: 12pt;
                    color: #666;
                }
                
                .po-supplier-info {
                    margin-bottom: 25px;
                    padding: 0;
                    border: 3px solid #000;
                    background: #fff;
                }
                
                .po-supplier-info h2 {
                    font-size: 13pt;
                    margin: 0;
                    padding: 12px 15px;
                    color: #fff !important;
                    background: #000 !important;
                    background-color: #000 !important;
                    font-weight: bold;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                
                .supplier-details {
                    padding: 20px 15px;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 15px;
                }
                
                .po-supplier-info .info-row {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                }
                
                .po-supplier-info .info-label {
                    font-weight: bold;
                    font-size: 9pt;
                    color: #666;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                
                .po-supplier-info .info-value {
                    color: #000;
                    font-size: 11pt;
                    font-weight: 600;
                    padding: 5px 0;
                    border-bottom: 1px solid #ddd;
                }
                
                .po-summary {
                    margin-bottom: 30px;
                    padding: 0;
                    background: #fff;
                    border: 3px solid #000;
                }
                
                .summary-header {
                    background: #000 !important;
                    background-color: #000 !important;
                    color: #fff !important;
                    padding: 10px 15px;
                    font-size: 12pt;
                    font-weight: bold;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                
                .summary-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 0;
                }
                
                .summary-grid.has-saldo {
                    grid-template-columns: repeat(3, 1fr);
                }
                
                .summary-row {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 0;
                    border-top: 2px solid #000;
                }
                
                .summary-item {
                    text-align: center;
                    padding: 20px 15px;
                    border-right: 2px solid #000;
                    position: relative;
                }
                
                .summary-item.highlight-green {
                    background: #f6ffed !important;
                    background-color: #f6ffed !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                
                .summary-item.highlight-green .value {
                    color: #389e0d;
                }
                
                .summary-item.highlight-red .value {
                    color: #cf1322;
                }
                
                .summary-item::after {
                    content: '';
                    position: absolute;
                    bottom: 10px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 40px;
                    height: 2px;
                    background: #000;
                }
                
                .summary-item .label {
                    font-size: 9pt;
                    color: #666;
                    margin-bottom: 10px;
                    font-weight: bold;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                
                .summary-item .value {
                    font-size: 18pt;
                    font-weight: bold;
                    color: #000;
                    margin-top: 10px;
                }
                
                .po-orders {
                    margin-bottom: 20px;
                    border: 3px solid #000;
                }
                
                .orders-header {
                    background: #000 !important;
                    background-color: #000 !important;
                    color: #fff !important;
                    padding: 12px 20px;
                    text-align: center;
                    font-weight: bold;
                    font-size: 11pt;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                
                .orders-header .count {
                    margin-left: 8px;
                    font-weight: normal;
                    opacity: 0.9;
                }
                
                .orders-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 9pt;
                }
                
                .orders-table th {
                    background: #000 !important;
                    background-color: #000 !important;
                    color: #fff !important;
                    padding: 8px 5px;
                    text-align: left;
                    border: 1px solid #000;
                    font-weight: bold;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                
                .orders-table td {
                    padding: 8px 5px;
                    border: 1px solid #000;
                    color: #000;
                }
                
                .orders-table .col-order {
                    width: 12%;
                }
                
                .orders-table .col-invoice {
                    width: 13%;
                }
                
                .orders-table .col-date {
                    width: 12%;
                }
                
                .orders-table .col-status {
                    width: 13%;
                    text-align: center;
                }
                
                .orders-table .col-total {
                    width: 15%;
                    text-align: right;
                }
                
                .orders-table .col-iva {
                    width: 15%;
                    text-align: right;
                }
                
                .orders-table .col-discount {
                    width: 10%;
                    text-align: right;
                }
                
                .orders-table .col-delivery {
                    width: 10%;
                    text-align: center;
                }
                
                .orders-table tfoot td {
                    font-weight: bold;
                    background: #000 !important;
                    background-color: #000 !important;
                    color: #fff !important;
                    font-size: 10pt;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                
                .po-footer {
                    margin-top: 30px;
                    padding-top: 10px;
                    border-top: 1px solid #000;
                    text-align: center;
                    font-size: 9pt;
                    color: #666;
                }
                
                .no-print {
                    display: none !important;
                }
            `
        });
    };

    useEffect(() => {
        if (isLoadingAuth || !interceptorsReady) {
            return;
        }

        const fetchData = async () => {
            try {
                // Get supplier ID from URL hash parameters
                const hashParts = window.location.hash.split('?');
                const queryString = hashParts.length > 1 ? hashParts[1] : window.location.search.substring(1);
                const params = new URLSearchParams(queryString);
                const supplierId = params.get('supplierId');

                if (!supplierId) {
                    setError('No se especificó el proveedor');
                    setLoading(false);
                    return;
                }

                // Set current date
                const now = new Date();
                setCurrentDate(now.toLocaleDateString('es-AR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }));

                // Fetch supplier orders (use reasonable limit to avoid 400 error)
                const response = await getPedidosBySupplier(supplierId, 1, 100, {});

                if (response && response.success) {
                    const supplierInfo = response.data?.supplier || response.supplier || null;
                    const summaryInfo = response.data?.summary || response.summary || null;
                    const ordersList = response.data?.orders || response.orders || [];
                    
                    setSupplierData(supplierInfo);
                    setSummary(summaryInfo);
                    setOrders(ordersList);
                } else {
                    setError('Error al cargar datos del proveedor');
                }

                setLoading(false);
            } catch (err) {
                console.error('[PrintPOSupplierPage] Error fetching data:', err);
                setError('Error al cargar datos: ' + (err.message || 'Error desconocido'));
                setLoading(false);
            }
        };

        fetchData();
    }, [isLoadingAuth, interceptorsReady]);

    // Calculate totals
    const totals = orders.reduce((acc, order) => {
        acc.total += Number(order.total) || 0;
        acc.iva += Number(order.iva) || 0;
        acc.discount += Number(order.discount) || 0;
        return acc;
    }, { total: 0, iva: 0, discount: 0 });

    if (loading) {
        return (
            <div className="print-po-supplier-container">
                <div className="po-header">
                    <h1>Cargando...</h1>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="print-po-supplier-container">
                <div className="po-header">
                    <h1>Error</h1>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="print-po-supplier-container no-print">
                <div className="po-header">
                    <h1>Vista Previa de Impresión</h1>
                    <p>Use Ctrl+P o el botón de imprimir del navegador para imprimir este documento.</p>
                    <button 
                        onClick={handlePrint} 
                        style={{
                            marginTop: '20px',
                            padding: '10px 20px',
                            fontSize: '16px',
                            backgroundColor: '#000',
                            color: '#fff',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        🖨️ Imprimir
                    </button>
                </div>
            </div>
            
            <div id="print-po-supplier-content" className="print-po-supplier-container">
                <div className="po-header">
                    <h1>ÓRDENES DE COMPRA POR PROVEEDOR</h1>
                    <div className="subtitle">Reporte Generado: {currentDate}</div>
                </div>

                {/* Supplier Info */}
                <div className="po-supplier-info">
                    <h2>Información del Proveedor</h2>
                    <div className="supplier-details">
                        <div className="supplier-detail-item razon-social">
                            <span className="label">Razón Social</span>
                            <span className="value">{supplierData?.supplier_razon_social || 'N/A'}</span>
                        </div>
                        <div className="supplier-detail-item">
                            <span className="label">CUIT</span>
                            <span className="value">{supplierData?.supplier_cuit || 'N/A'}</span>
                        </div>
                        <div className="supplier-detail-item">
                            <span className="label">ID Proveedor</span>
                            <span className="value">{supplierData?.supplier_id || 'N/A'}</span>
                        </div>
                        <div className="supplier-detail-item">
                            <span className="label">Fecha Reporte</span>
                            <span className="value">{currentDate}</span>
                        </div>
                    </div>
                </div>

                {/* Summary */}
                {(() => {
                    const saldoAFavor = Math.max(0, (summary?.paid_total || 0) - (summary?.all?.total || 0));
                    return (
                        <div className="po-summary">
                            <div className="summary-header">Resumen General</div>
                            <div className="summary-grid">
                                <div className="summary-item">
                                    <div className="label">Total Órdenes</div>
                                    <div className="value">{summary?.all?.count || 0}</div>
                                </div>
                                <div className="summary-item">
                                    <div className="label">Monto Total</div>
                                    <div className="value">{formatPrice(summary?.all?.total || 0)}</div>
                                </div>
                                <div className="summary-item">
                                    <div className="label">IVA Total</div>
                                    <div className="value">{formatPrice(summary?.all?.iva || 0)}</div>
                                </div>
                            </div>
                            <div className="summary-row">
                                <div className="summary-item highlight-green">
                                    <div className="label">Pagado</div>
                                    <div className="value">{formatPrice(summary?.paid_total || 0)}</div>
                                </div>
                                <div className="summary-item highlight-red">
                                    <div className="label">Pendiente de Pago</div>
                                    <div className="value">{formatPrice(summary?.pending_total || 0)}</div>
                                </div>
                                {saldoAFavor > 0 ? (
                                    <div className="summary-item highlight-green">
                                        <div className="label">Saldo a Favor</div>
                                        <div className="value">{formatPrice(saldoAFavor)}</div>
                                    </div>
                                ) : (
                                    <div className="summary-item" />
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Orders Table */}
                <div className="po-orders">
                    <div className="orders-header">
                        Órdenes de Compra<span className="count">({orders.length})</span>
                    </div>
                    <table className="orders-table">
                        <thead>
                            <tr>
                                <th className="col-order">N° Orden</th>
                                <th className="col-invoice">Factura Prov.</th>
                                <th className="col-date">Fecha</th>
                                <th className="col-status">Estado</th>
                                <th className="col-total">Total</th>
                                <th className="col-iva">IVA</th>
                                <th className="col-discount">Descuento</th>
                                <th className="col-delivery">Entrega</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => (
                                <tr key={order.id}>
                                    <td className="col-order">{order.order_number}</td>
                                    <td className="col-invoice">{order.related_supplier_invoice || '-'}</td>
                                    <td className="col-date">{formatDate(order.order_date)}</td>
                                    <td className="col-status">{getStatusLabel(order.status)}</td>
                                    <td className="col-total">{formatPrice(order.total)}</td>
                                    <td className="col-iva">{formatPrice(order.iva)}</td>
                                    <td className="col-discount">{formatPrice(order.discount)}</td>
                                    <td className="col-delivery">{formatDate(order.actual_delivery_date)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan="4" style={{ textAlign: 'right' }}>TOTALES:</td>
                                <td className="col-total">{formatPrice(totals.total)}</td>
                                <td className="col-iva">{formatPrice(totals.iva)}</td>
                                <td className="col-discount">{formatPrice(totals.discount)}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Footer */}
                <div className="po-footer">
                    <p>Horizonte PyME - Sistema de Gestión</p>
                    <p>Documento generado automáticamente el {currentDate}</p>
                </div>
            </div>
        </>
    );
}

export default PrintPOSupplierPage;
